import { useRef, useState } from 'react';
import { cropImageToSquare } from '@/lib/storage';

interface Props {
  label?: string;
  onCropped: (blob: Blob, previewUrl: string) => void;
  onCroppedMany?: (items: Array<{ blob: Blob; previewUrl: string }>) => void;
  multiple?: boolean;
  aspect?: 'square' | 'free';
  /** When true, parent renders the preview — avoids duplicate images. */
  hidePreview?: boolean;
}

export function ImageCropper({ label = 'Choose photo', onCropped, onCroppedMany, multiple = false, hidePreview }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const next: Array<{ blob: Blob; previewUrl: string }> = [];
      for (const file of Array.from(files)) {
        const blob = await cropImageToSquare(file);
        const url = URL.createObjectURL(blob);
        next.push({ blob, previewUrl: url });
      }
      if (!next.length) return;
      setPreview(next[0].previewUrl);
      onCropped(next[0].blob, next[0].previewUrl);
      onCroppedMany?.(next);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <input ref={inputRef} type="file" accept="image/*" multiple={multiple} className="hidden" onChange={(e) => void handleFiles(e.target.files)} />
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        className="w-full rounded-xl border border-dashed border-slate-300 bg-slate-50 py-8 text-sm font-semibold text-slate-600"
      >
        {busy ? 'Processing…' : label}
      </button>
      {preview && !hidePreview && <img src={preview} alt="" className="w-full rounded-xl object-cover" />}
    </div>
  );
}
